import { OperationSummaryBuilder } from './../../models/builders/operation-summary.builder';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, take, shareReplay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ManufacturingOrderBuilder } from '../../models/builders/manufacturing-order.builder';
import { SchedulerTaskBuilder } from '../../models/builders/scheduler-task.builder';
import { PlanFileBuilder } from '../../models/builders/plan-file.builder';
import { ManufacturingOrderFilterBuilder } from '../../models/builders/manufacturing-order-filter.builder';
import { OperationSchedulerTasksBuilder } from '../../models/builders/operation-scheduler-task.builder';
import { ManufacturingOrderPageableBuilder } from '../../models/builders/manufacturing-order-pageable.builder';

const editManufacturingOrderMaterial = (
  material: string,
  materialHash: string,
  materialDeliveryDate: number
) => {
  return {
    material: material,
    materialHash: materialHash,
    materialDeliveryDate: materialDeliveryDate,
  };
};

@Injectable()
export class ManufacturingOrderArticleApiService {
  private apiUrl: string;

  constructor(private httpClient: HttpClient) {
    this.apiUrl = `${environment.apiUrl}manufacturing-orders`;
  }

  getManufacturingOrders(page: number, size: number) {
    const params = new HttpParams().append('page', page.toString()).append('size', size.toString());

    return this.httpClient
      .get(this.apiUrl, { params })
      .pipe(map(ManufacturingOrderPageableBuilder.fromJson));
  }


  searchManufacturingOrdersPaginable(chain: string, page: number, size: number, status?: string) {
    let params = new HttpParams().append('search', chain)
                  .append('page', page.toString()).append('size', size.toString());
    if (status) {
      params = params.append('status', status);
    }
    return this.httpClient
      .get(`${this.apiUrl}/search-pageable`, { params })
      .pipe(map(ManufacturingOrderPageableBuilder.fromJson));
  }

  getManufacturingOrdersNotScheduled() {
    return this.httpClient
      .get(`${this.apiUrl}/not-scheduled`)
      .pipe(map(ManufacturingOrderBuilder.fromList));
  }

  getManufacturingOrdersNotScheduledByModel(model: string) {
    return this.httpClient
      .get(`${this.apiUrl}/not-scheduled/${model}`)
      .pipe(map(ManufacturingOrderBuilder.fromList));
  }

  getManufacturingOrdersOpened() {
    return this.httpClient
      .get(`${this.apiUrl}/opened`)
      .pipe(map(ManufacturingOrderBuilder.fromList));
  }

  getFile(fileUrl: string) {
    return this.httpClient.get(fileUrl, { responseType: 'arraybuffer', headers: new HttpHeaders(
      { 'Cache-Control': 'no-cache' }) });
  }

  searchManufacturingOrders(chain: string, status?: string) {
    let params = new HttpParams().append('search', chain);
    if (status) {
      params = params.append('status', status);
    }
    return this.httpClient
      .get(`${this.apiUrl}/search`, { params })
      .pipe(map(ManufacturingOrderBuilder.fromList), take(5), shareReplay(1));
  }

  getManufacturingOrderFilters() {
    return this.httpClient
      .get(`${this.apiUrl}/filter`)
      .pipe(map(ManufacturingOrderFilterBuilder.fromList));
  }

  getSearchRegisteredManufacturingOrdersWithTask(search: string) {
    const params = new HttpParams().append('search', search);
    return this.httpClient
      .get(`${this.apiUrl}/search-with-schedulerTask`, { params })
      .pipe(
        map(ManufacturingOrderBuilder.buildRegisteredManufacturingOrderFromList)
      );
  }

  getSearchManufacturingOrdersWithTask(search: string) {
    const params = new HttpParams().append('search', search);
    return this.httpClient
      .get(`${this.apiUrl}/search-with-schedulerTask`, { params })
      .pipe(map(ManufacturingOrderBuilder.fromList));
  }

  getManufacturingOrder(hash: string) {
    return this.httpClient
      .get(`${this.apiUrl}/${hash}`)
      .pipe(map(ManufacturingOrderBuilder.fromJson));
  }

  getManufacturingOrderWithOperationToPlan(hash: string) {
    return this.httpClient
      .get(`${this.apiUrl}/${hash}/operations-to-plan`)
      .pipe(map(ManufacturingOrderBuilder.fromJson));
  }

  getOperationInfo(manufacturingOrderHash: string, operationHash: string) {
    return this.httpClient
      .get(
        `${this.apiUrl}/${manufacturingOrderHash}/operation-process/${operationHash}/task-info`
      )
      .pipe(map(OperationSummaryBuilder.fromJson));
  }

  getManufacturingOrderPlane(hash: string) {
    return this.httpClient
      .get(`${this.apiUrl}/${hash}/plan`)
      .pipe(map(PlanFileBuilder.fromJson));
  }

  generateManufacturingOrderPDF(hash: any) {
    return this.httpClient.post(
      `${this.apiUrl}/${hash}/generate-pdf`,
      {},
      { responseType: 'arraybuffer' }
    );
  }

  getSchedulerTaskByManufacturingOrderAndOperation(
    manufacturingOrderHash: string,
    operationProcessHash: string
  ) {
    return this.httpClient
      .get(
        `${this.apiUrl}/${manufacturingOrderHash}/operation-process/${operationProcessHash}/scheduler-task`
      )
      .pipe(map(SchedulerTaskBuilder.fromJson));
  }

  editManufacturingOrderMaterial(
    hash: string,
    material: string,
    materialHash: string,
    materialDeliveryDate: number
  ) {
    return this.httpClient.post(
      `${this.apiUrl}/${hash}/material`,
      editManufacturingOrderMaterial(
        material,
        materialHash,
        materialDeliveryDate
      )
    );
  }

  getOperationSchedulerTasksByManufacturingOrder(hash: string) {
    return this.httpClient
      .get(`${this.apiUrl}/${hash}/operation-scheduler-tasks`)
      .pipe(map(OperationSchedulerTasksBuilder.fromList));
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/services/api/manufacturing-order-article-api.service.ts